/* eslint-disable @typescript-eslint/no-use-before-define */
import { datadogRum } from '@datadog/browser-rum'
import {
  Icomponent,
  IConfig,
  IContainers,
  IContainersObject,
  IModal,
  IModalsObject,
  ITextLibrary,
  Localization,
} from '../interfaces'
import {
  DEFAULT_CONTAINERS_PROPS,
  DEFAULT_MODALS_PROPS,
} from '../consts/defaultConfigValues/defaultContainersProps'
import DEFAULT_COMPONENTS_PROPS from '../consts/defaultConfigValues/defaultProps'
import defaultConfig from '../consts/defaultConfig'
import defaultTexts from '../consts/defaultConfigValues/defaultTexts'
import deepMerge from './deepMerge'
import processData from './processData'
import { withResolvedRecommendationLimits } from './recommendationLimits'
import { TextDirMap } from '../consts/defaultConfigValues/defaultConstants'
import { generateDataDogContext } from './dataDog'
import { describeType, isObject } from './validTypes'

/**
 * Returns a processed config with default props and some initial props pre-processed
 * @param config original config data
 * @param reportContext extra Datadog facets for the reports raised while
 * processing. The caller passes them when the URL no longer names this
 * config's storefront, which is what the report context reads by default.
 */
export function processConfig(
  config: IConfig,
  language?: Localization,
  reportContext: Record<string, unknown> = {},
): IConfig {
  const mergedConfig = deepMerge(defaultConfig, config)
  // Before `processContainers` below, which eagerly resolves every
  // `{{config.globals...}}` binding against this object and bakes the result
  // into component props. Correcting a limit after that point corrects it only
  // where nothing reads it.
  mergedConfig.globals = withResolvedRecommendationLimits(mergedConfig.globals)
  // `deepMerge` ends with `{ ...obj1, ...newObject }`, so for a top-level key
  // the remote config does not author (`containers` or `modals` on a storefront
  // that writes neither), it hands back `defaultConfig`'s own object by
  // reference. `processContainers` then mutates that collection in place —
  // COM-1346 pins `kept === collection` so a fix inside it would break the
  // contract — and the writes land on the module-level singleton. On the first
  // load that overwrites the shared defaults with entries whose bindings are
  // already resolved against this storefront's config; on the second load in
  // the same page (slug change) Immer has auto-frozen those objects in the
  // store and the write throws `Cannot assign to read only property`. The
  // identity check fires only in that case — a storefront that authors the
  // collection has gone through `deepMerge`'s recursion into a fresh object,
  // and the array/string shapes COM-1346 preserves (`describeCollection`,
  // `routedSkippedContainers`, the `collectionType` facet) must not be
  // broadened into plain objects by a blanket shallow copy. See COM-1351.
  if (mergedConfig.containers === defaultConfig.containers) {
    mergedConfig.containers = { ...mergedConfig.containers }
  }
  if (
    mergedConfig.modals &&
    mergedConfig.modals === defaultConfig.modals
  ) {
    mergedConfig.modals = { ...mergedConfig.modals }
  }
  let { containers, modals } = mergedConfig
  const { options = {}, texts: configTexts, globals } = mergedConfig
  const customLanguage: ITextLibrary = language
    ? (configTexts[language] as unknown as ITextLibrary)
    : configTexts
  const localization = language || options.localization || Localization.En
  const textDir = options.textDir || TextDirMap[localization]
  const texts = defaultTexts[localization] || defaultTexts.en
  const mergedTexts = deepMerge(texts, customLanguage)

  const authoredContainers = containers
  const processedContainers = processContainers(
    containers,
    DEFAULT_CONTAINERS_PROPS,
    mergedConfig,
  )
  containers = processedContainers.kept as IContainersObject
  const routedLost = routedSkippedContainers(
    mergedConfig.routes,
    authoredContainers,
    processedContainers,
  )
  if (routedLost.length > 0) {
    throw new Error(
      describeRoutedSkip(
        routedLost,
        authoredContainers,
        processedContainers.skipped,
      ),
    )
  }
  reportSkippedEntries(
    'containers',
    authoredContainers,
    processedContainers.skipped,
    reportContext,
  )

  if (modals) {
    const authoredModals = modals
    const processedModals = processContainers(
      modals,
      DEFAULT_MODALS_PROPS,
      mergedConfig,
    )
    modals = processedModals.kept as IModalsObject
    reportSkippedEntries(
      'modals',
      authoredModals,
      processedModals.skipped,
      reportContext,
    )
  }

  const { footer } = globals

  const newGlobals = {
    ...globals,
    footer: {
      ...footer,
      priceAlign: textDir === 'rtl' ? 'left' : 'right',
    },
  }

  return {
    ...mergedConfig,
    globals: newGlobals,
    containers,
    modals,
    texts: mergedTexts,
    options: {
      ...options,
      localization,
      textDir,
    },
  }
}

/** An entry `processContainers` left out, and what it threw. */
export interface SkippedEntry {
  key: string
  message: string
}

/** The longest list of keys a report names; the full count travels beside it. */
export const MAX_REPORTED_KEYS = 20

const errorMessage = (error: unknown) =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error)

/**
 * Process the data of the containers or modals in a config, including the components
 *
 * Each entry is processed on its own, and an entry whose processing throws —
 * a missing `body`, `components` that are not a list, a binding that cannot
 * be resolved, or anything else — is left out of `kept` and listed in
 * `skipped` rather than failing the whole load. That throw cannot have been
 * load-bearing: it rejected the config load with the boot loader still up, so
 * the storefront stayed on its splash. The caller decides what a skip costs:
 * see `processConfig`.
 *
 * Nothing is written to an entry until its processing has finished, and the
 * processing itself writes nothing shared (`deepMerge`, `processData` and
 * `getComponentsProps` all build new objects), so an entry that throws leaves
 * no partial state behind and every entry that does not throw comes out
 * exactly as it did before. The write-back stays outside the `try` on
 * purpose: it is not about the entry's shape, and a collection that refuses
 * the write should fail as loudly as it always has.
 * @param containers Object of containers or modals
 * @param defaultProps List of default props for the container
 * @param config Config data, to be used as data reference
 * @returns `kept`: the collection itself when nothing was skipped, otherwise a
 * fresh plain object without the skipped keys (the collection may be an array
 * or a string, whose indexes cannot be deleted, and a skipped key left in
 * place would still be registered by `setModals`)
 */
export function processContainers(
  containers: IContainersObject | IModalsObject,
  defaultProps: typeof DEFAULT_CONTAINERS_PROPS | typeof DEFAULT_MODALS_PROPS,
  config: IConfig,
): {
  kept: IContainersObject | IModalsObject
  skipped: SkippedEntry[]
} {
  const skipped: SkippedEntry[] = []
  Object.keys(containers).forEach((key) => {
    let processed: IContainers | IModal
    try {
      processed = processEntry(containers[key], key, defaultProps, config)
    } catch (error) {
      skipped.push({ key, message: errorMessage(error) })
      return
    }
    containers[key] = processed
  })
  if (skipped.length === 0) {
    return { kept: containers, skipped }
  }
  const skippedKeys = new Set(skipped.map(({ key }) => key))
  const kept = Object.fromEntries(
    Object.keys(containers)
      .filter((key) => !skippedKeys.has(key))
      .map((key) => [key, containers[key]]),
  ) as IContainersObject | IModalsObject
  return { kept, skipped }
}

/**
 * One entry of `processContainers`: its defaults merged in, then the
 * components of its header, body and footer. Throws for anything it cannot
 * process; the caller catches.
 */
const processEntry = (
  entry: IContainers | IModal,
  key: string,
  defaultProps: typeof DEFAULT_CONTAINERS_PROPS | typeof DEFAULT_MODALS_PROPS,
  config: IConfig,
): IContainers | IModal => {
  let container: IContainers | IModal = {
    ...entry,
  }
  const { skipDefaults } = container
  const containerDefaults = defaultProps[key]
  // process default props of the container
  if (containerDefaults && !skipDefaults) {
    container = deepMerge(containerDefaults, container) as IContainers | IModal
  }
  // process default props for header, body and footer components
  const { header, body, footer } = container

  const newHeader = header
    ? {
        ...header,
        components: getComponentsProps(header.components, config),
      }
    : undefined

  const newBody = {
    ...body,
    components: getComponentsProps(body.components, config),
  }

  const newFooter = footer
    ? {
        ...footer,
        components: getComponentsProps(footer.components, config),
      }
    : undefined

  return {
    ...container,
    header: newHeader,
    body: newBody,
    footer: newFooter,
  }
}

/**
 * The containers a route names that the boot cannot render: skipped by
 * `processContainers`, or — when the authored collection was not a plain
 * object and so carries no names at all — absent from what was kept. Those
 * would reach `BaseContainer` as `undefined` and throw into the bare
 * `ErrorBoundaries` fallback, which has no retry, so `processConfig` fails the
 * load instead and the shopper gets the reloadable boot error.
 *
 * A route naming a container that a plain-object collection never authored is
 * deliberately not counted: that is not something this function skipped, and
 * how it renders predates the skip. Nor is anything counted when nothing was
 * skipped, so a collection that processed whole behaves as it always did
 * whatever its shape. Defensive about `routes` itself (not an array, entries
 * without a `container`) because this check must not add a throw of its own.
 */
const routedSkippedContainers = (
  routes: unknown,
  authored: unknown,
  { kept, skipped }: { kept: unknown; skipped: SkippedEntry[] },
) => {
  if (skipped.length === 0 || !Array.isArray(routes)) {
    return []
  }
  const skippedKeys = new Set(skipped.map(({ key }) => key))
  const lostWhenAbsent = !isObject(authored)
  const named = new Set<string>()
  routes.forEach((route) => {
    const name = (route as { container?: unknown } | null)?.container
    if (typeof name === 'string') {
      named.add(name)
    }
  })
  return [...named].filter(
    (name) =>
      skippedKeys.has(name) ||
      (lostWhenAbsent && !Object.prototype.hasOwnProperty.call(kept, name)),
  )
}

const capKeys = (keys: string[]) => {
  const listed = keys.slice(0, MAX_REPORTED_KEYS)
  const more = keys.length - listed.length
  return `${JSON.stringify(listed)}${more > 0 ? ` and ${more} more` : ''}`
}

/**
 * The message of the error `processConfig` throws when a route names a
 * container it could not process. It is the only report of that load — the
 * per-collection skip report is not also sent, since the boot fails anyway
 * and `fetchConfig` reports this error — so it carries everything that report
 * would have: the routed keys, every other skipped container, and the first
 * thing that threw.
 */
const describeRoutedSkip = (
  routed: string[],
  authored: unknown,
  skipped: SkippedEntry[],
) => {
  const routedSet = new Set(routed)
  const others = skipped
    .map(({ key }) => key)
    .filter((key) => !routedSet.has(key))
  const shape = isObject(authored)
    ? ''
    : ` (containers is a ${describeType(authored)}, not an object)`
  return [
    `containers ${capKeys(routed)} are named by a route but could not be processed${shape}; the storefront cannot boot without them`,
    others.length > 0
      ? `; ${others.length} unrouted container(s) were also skipped: ${capKeys(others)}`
      : '',
    skipped.length > 0 ? `. First error: ${skipped[0].message}` : '',
  ].join('')
}

/**
 * One Datadog error per collection, not per entry: a string authored as
 * `modals` is walked character by character, and each character is an entry.
 * At most `MAX_REPORTED_KEYS` keys are listed; `skippedCount` carries the
 * full count. A skipped modal becomes an unauthored name, which is reported
 * again if anything opens it.
 */
const reportSkippedEntries = (
  collection: 'containers' | 'modals',
  authored: unknown,
  skipped: SkippedEntry[],
  reportContext: Record<string, unknown>,
) => {
  if (skipped.length === 0) {
    return
  }
  const listed = skipped.slice(0, MAX_REPORTED_KEYS)
  const extraInfo: Record<string, unknown> = {
    function: 'processContainers',
    collection,
    keys: listed.map(({ key }) => key),
    errors: listed.map(({ message }) => message),
    skippedCount: skipped.length,
    ...reportContext,
  }
  if (!isObject(authored)) {
    extraInfo.collectionType = describeType(authored)
  }
  datadogRum.addError(
    new Error(
      `${collection} ${capKeys(skipped.map(({ key }) => key))} could not be processed and were skipped; the storefront boots without them. First error: ${skipped[0].message}`,
    ),
    generateDataDogContext({
      title: `${collection} entry malformed`,
      extraInfo,
    }),
  )
}

/**
 * Process the props of a component, merging the default props and then pre-processing some initial variables
 * @param components List of components
 * @param config Config data, to be used as data reference
 */
export function getComponentsProps(
  components: Icomponent[],
  config: IConfig,
): Icomponent[] {
  return components?.map((component) => {
    const source = { state: { defaultSlice: { config } } }
    const { defaultProps, props = {} } = component
    const defaults = DEFAULT_COMPONENTS_PROPS[
      defaultProps!
    ] as Icomponent['props']
    const processedDefaults = processData(defaults || {}, source, true)
    const processedProps = processData(props, source, true)
    const newProps = defaultProps
      ? deepMerge(processedDefaults, processedProps)
      : processedProps
    return {
      ...component,
      props: newProps,
    }
  })
}

export default processConfig
